import {
  RXDB_VMP_COLLECTION_NAME,
  type Vmp,
} from "$global-providers/rxdb-provider/collections/vmp";
import { dateYearsMonths } from "$routes/_lib/helpers";
import { useUserForPrescriptionStatusQuery } from "$routes/patient/_lib/__generated__/prescription-history-card.gql";
import { dosageCount } from "$routes/patient/_lib/styled-dosage-string";
import { fieldFormatters } from "$routes/patient/[:patientId]/antenatal-care/_lib/constants";
import { InvestigationActivityResults } from "$routes/patient/[:patientId]/investigations/_lib/investigation-activity-results";
import { Box, styled, Typography } from "@material-ui/core";
import type { Gender } from "elephant-web/gql";
import { omit } from "ramda";
import React, { type FC, useEffect, useRef } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import ReactToPrint from "react-to-print";
import { useRxData } from "rxdb-hooks";
import { ReportOption } from "./download-report-modal";
import {
  HistoricPatientEvent,
  type ImagingStudyWithImages,
  isAncEncounter,
  isConsultation,
  isImagingStudy,
  isRxInvestigationActivity,
  isRxPrescription,
  isVisit,
  type RxPrescriptionWithDispensationsAndLegacyCreated,
} from "./types";

const ContainerWrapper = styled("div")({
  display: "none",
});

const PrintWrapper = styled("div")({
  margin: "5mm",
});

const StyledBox = styled(Box)(({ theme }) => ({
  margin: "0 15px",
  padding: "10px 0",
}));

const SubTitle = styled("strong")({
  fontSize: 16,
});

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  marginBottom: theme.spacing(1),
}));

const ResultsBox = styled(Box)({
  width: "50%",
  alignSelf: "flex-end",
});

const Title = styled("h1")({
  width: "70%",
  margin: 0,
});

const CardIdWrapper = styled("div")({
  width: "30%",
  textAlign: "right",
});

const PrescriptionDetailsWrapper = styled("div")({
  width: "60%",
  margin: 0,
});

const PrescriberWrapper = styled("div")({
  width: "40%",
  alignSelf: "flex-end",
});

const PatientInfo = styled("p")(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(1),
}));

const PatientWrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const Section = styled("div")(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const Clinic = styled("h2")(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const SectionTitle = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const History = styled("div")({
  whiteSpace: "pre-wrap",
});

const Footer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const FooterWrapper = styled("div")({
  float: "right",
});

const ImagingGrid = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const ImagingThumbnail = styled("img")(({ theme }) => ({
  width: 80,
  height: 80,
  objectFit: "cover",
  borderRadius: 4,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const ImagingThumbnailPlaceholder = styled("div")(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${theme.palette.grey[300]}`,
}));

interface ComponentToPrintProps {
  date: string;
  cardId: string;
  clinicName: string;
  patient?: {
    name: string;
    gender: Gender;
    dob: Date;
    phoneNumber?: string;
  };
  historicEvents: HistoricPatientEvent[];
  referrals: Array<{
    facilityName?: string;
    reasons: string[];
  }>;
  reportOption: ReportOption;
}

/**
 * Iterate array of cardEntities until finding one with a clinician
 */
function findClinicianName(historicEvents: HistoricPatientEvent[]): string {
  for (const historicEvent of historicEvents) {
    if (isConsultation(historicEvent)) {
      const clinician = historicEvent.deliveredBy;

      if (clinician) {
        return [clinician.person.firstname, clinician.person.lastname]
          .filter(Boolean)
          .join(" ");
      }
    }

    if (isVisit(historicEvent)) {
      const name = historicEvent.admissionEvent?.performer?.name;
      if (name) {
        return name;
      }
    }
  }

  return undefined;
}

const ImagingStudySection: FC<{
  study: ImagingStudyWithImages;
}> = ({ study }) => {
  return (
    <Section>
      <h2>
        <FormattedMessage
          id="patient.history.report.imaging"
          defaultMessage="Imaging Study"
        />
      </h2>
      <SectionTitle>
        <SubTitle>
          <FormattedMessage
            id="patient.history.report.imaging.received"
            defaultMessage="Received:"
          />{" "}
          <FormattedDate value={study.startedAt} />
        </SubTitle>
      </SectionTitle>
      <SectionTitle>
        <SubTitle>
          <FormattedMessage
            id="patient.history.report.imaging.from"
            defaultMessage="From:"
          />{" "}
          <FormattedMessage
            id="patient.history.report.imaging.externalSource"
            defaultMessage="External source"
          />
        </SubTitle>
      </SectionTitle>
      {study.images.length > 0 && (
        <ImagingGrid>
          {study.images.map((image) => (
            <div key={image.id}>
              {image.thumbnailUrl ? (
                <ImagingThumbnail
                  src={image.thumbnailUrl}
                  alt={`Imaging study ${image.id}`}
                />
              ) : (
                <ImagingThumbnailPlaceholder>
                  <Typography variant="caption" color="textSecondary">
                    No Image
                  </Typography>
                </ImagingThumbnailPlaceholder>
              )}
            </div>
          ))}
        </ImagingGrid>
      )}
    </Section>
  );
};

export const ComponentToPrint = React.forwardRef<
  HTMLDivElement,
  ComponentToPrintProps
>(
  (
    {
      date,
      cardId,
      clinicName,
      patient,
      historicEvents,
      referrals,
      reportOption,
    },
    ref,
  ) => {
    const clinicianName = findClinicianName(historicEvents);
    const firstConsultation = historicEvents[0];

    return (
      <PrintWrapper ref={ref}>
        <StyledBox>
          <Container>
            <Title>
              <FormattedMessage
                id="patient.history.report.doctorsReport"
                defaultMessage="Doctor's Report"
              />
              {" - "}
              {isVisit(firstConsultation) ? (
                <FormattedMessage
                  id="eclaims.print.inpatient"
                  defaultMessage="Inpatient"
                />
              ) : (
                <FormattedMessage
                  id="eclaims.print.outpatient"
                  defaultMessage="Outpatient"
                />
              )}
            </Title>
            <CardIdWrapper>
              <span>
                <FormattedMessage
                  id="patient.history.report.id"
                  defaultMessage="ID:"
                />
                {cardId}
              </span>
            </CardIdWrapper>
          </Container>
          <Clinic>{clinicName}</Clinic>
          <SubTitle>{date}</SubTitle>
          <PatientWrapper>
            <PatientInfo>
              <strong>
                <FormattedMessage
                  id="patient.history.report.name"
                  defaultMessage="Name:"
                />
              </strong>
              {patient.name}
            </PatientInfo>
            <PatientInfo>
              <strong>
                <FormattedMessage
                  id="patient.history.report.age"
                  defaultMessage="Age:"
                />
              </strong>
              {` ${dateYearsMonths(patient.dob)}`}
            </PatientInfo>
            <PatientInfo>
              <strong>
                <FormattedMessage
                  id="patient.history.report.gender"
                  defaultMessage="Gender:"
                />
              </strong>
              {patient.gender === "m" ? " Male" : " Female"}
            </PatientInfo>
            {patient.phoneNumber && (
              <PatientInfo>
                <strong>
                  <FormattedMessage
                    id="patient.history.report.phone"
                    defaultMessage="Phone:"
                  />
                </strong>
                {` ${patient.phoneNumber}`}
              </PatientInfo>
            )}
            {isVisit(firstConsultation) && (
              <>
                {firstConsultation.admissionEvent && (
                  <PatientInfo data-testid="printable-summary-admission-timestamp">
                    <strong>
                      <FormattedMessage
                        id="eclaims.print.inpatient.admission"
                        defaultMessage="Admitted On: "
                      />
                    </strong>
                    <FormattedDate
                      year="numeric"
                      month="numeric"
                      day="numeric"
                      hour="numeric"
                      minute="numeric"
                      value={firstConsultation.admissionEvent.created}
                    />
                  </PatientInfo>
                )}
                {firstConsultation.dischargeEvent && (
                  <PatientInfo data-testid="printable-summary-discharge-timestamp">
                    <strong>
                      <FormattedMessage
                        id="eclaims.print.inpatient.discharge"
                        defaultMessage="Discharged On: "
                      />
                    </strong>
                    <FormattedDate
                      year="numeric"
                      month="numeric"
                      day="numeric"
                      hour="numeric"
                      minute="numeric"
                      value={firstConsultation.dischargeEvent.created}
                    />
                  </PatientInfo>
                )}
              </>
            )}
          </PatientWrapper>
          {reportOption === ReportOption.WithHistory && (
            <Section>
              <h2>
                <FormattedMessage
                  id="patient.history.report.problem"
                  defaultMessage="History and examination"
                />
              </h2>
              {historicEvents?.map((historicEvent) =>
                isConsultation(historicEvent) && !!historicEvent.problem ? (
                  <History key={`${historicEvent.uuid}-problem`}>
                    <Typography variant="body2">
                      {historicEvent.problem}
                    </Typography>
                  </History>
                ) : null,
              )}
            </Section>
          )}
          <div>
            {historicEvents.map((historicEvent, _index) => {
              const investigationOutcome = isRxInvestigationActivity(
                historicEvent,
              )
                ? historicEvent.outcome
                : undefined;
              const investigationFulfiller = investigationOutcome?.performer;
              const investigationFulfilledDate =
                investigationOutcome?.meta?.created ??
                ("meta" in historicEvent
                  ? historicEvent.meta?.created
                  : undefined);

              const key =
                (isRxInvestigationActivity(historicEvent) &&
                  historicEvent?.id) ||
                (isConsultation(historicEvent) && historicEvent?.uuid) ||
                (isAncEncounter(historicEvent) && historicEvent?.id);

              return (
                <div key={`${key}-root`}>
                  {isRxInvestigationActivity(historicEvent) && (
                    <Section>
                      <h2>
                        <FormattedMessage
                          id="patient.history.report.investigations"
                          defaultMessage="Investigation"
                        />
                      </h2>
                      <SectionTitle>
                        <SubTitle>
                          {historicEvent.catalogueItem?.title?.en}
                        </SubTitle>
                      </SectionTitle>
                      <Container>
                        <ResultsBox>
                          <InvestigationActivityResults
                            property={omit(
                              ["meta"],
                              historicEvent.outcome ?? historicEvent.request,
                            )}
                            print
                          />
                        </ResultsBox>
                        <ResultsBox textAlign="right">
                          {historicEvent.outcome?.performer ? (
                            <Typography variant="body2">
                              <strong>
                                <FormattedMessage
                                  id="patient.history.report.requestedBy"
                                  defaultMessage="Requested by:"
                                />
                              </strong>
                              {investigationFulfiller?.name
                                ? ` ${investigationFulfiller.name}${
                                    investigationFulfilledDate ? ", " : ""
                                  }`
                                : ""}
                              {investigationFulfilledDate ? (
                                <FormattedDate
                                  value={investigationFulfilledDate}
                                />
                              ) : null}
                            </Typography>
                          ) : null}
                        </ResultsBox>
                      </Container>
                    </Section>
                  )}
                  {isConsultation(historicEvent) && (
                    <Section>
                      <h2>
                        <FormattedMessage
                          id="patient.history.report.diagnosis"
                          defaultMessage="Diagnosis"
                        />
                      </h2>
                      {historicEvent.diseases?.map((disease) => (
                        <div key={disease.id}>
                          <SubTitle>{disease.displayName}</SubTitle>
                        </div>
                      ))}
                      {historicEvent.diagnoses?.map((diagnosis) => (
                        <div key={diagnosis.uuid}>
                          <SubTitle>{diagnosis.codification.name}</SubTitle>
                        </div>
                      ))}
                    </Section>
                  )}
                  {isRxPrescription(historicEvent) && (
                    <PrescriptionDetails prescription={historicEvent} />
                  )}
                  {isAncEncounter(historicEvent) && (
                    <Section key={historicEvent.id}>
                      <h2>
                        <FormattedMessage
                          id="patient.history.report.anc"
                          defaultMessage="Antenatal Care"
                        />
                      </h2>
                      {Object.entries(historicEvent)
                        .filter(([k]) => fieldFormatters[k] != null)
                        .map(([k, value]) => {
                          const field = fieldFormatters[k];
                          return (
                            <Box key={`${historicEvent.id}-${k}`} my={2}>
                              <SubTitle>
                                <FormattedMessage {...field.label} />
                              </SubTitle>
                              <Typography variant="body2" component="p">
                                {field.format(value)}
                              </Typography>
                            </Box>
                          );
                        })}
                      {historicEvent.prescriptions?.length && (
                        <Section>
                          <h2>
                            <FormattedMessage
                              id="patient.history.report.prescription"
                              defaultMessage="Prescription"
                            />
                          </h2>
                          {historicEvent.prescriptions?.map((prescription) => (
                            <div key={prescription.id}>
                              <SectionTitle>
                                <SubTitle>
                                  {prescription.clinicalProduct?.title ?? ""}
                                </SubTitle>
                              </SectionTitle>
                              <Container>
                                <PrescriptionDetailsWrapper>
                                  <div>
                                    <Typography variant="body2" component="p">
                                      <strong>
                                        <FormattedMessage
                                          id="patient.history.report.anc.datePrescribed"
                                          defaultMessage="Date prescribed:"
                                        />
                                      </strong>
                                      &nbsp;
                                      <FormattedDate
                                        value={historicEvent.meta.created}
                                      />
                                    </Typography>
                                  </div>
                                  <div>
                                    <Typography variant="body2" component="p">
                                      <strong>
                                        <FormattedMessage
                                          id="patient.history.report.anc.singleDosage"
                                          defaultMessage="Single dosage:"
                                        />
                                        &nbsp;
                                      </strong>
                                      {prescription.dosage
                                        ? `${prescription.dosage.value} ${prescription.dosage.unit}`
                                        : null}
                                    </Typography>
                                  </div>
                                </PrescriptionDetailsWrapper>
                                <PrescriberWrapper>
                                  <div>
                                    <Typography variant="body2" component="p">
                                      <strong>
                                        <FormattedMessage
                                          id="patient.history.report.anc.prescribedBy"
                                          defaultMessage="Prescribed by:"
                                        />
                                      </strong>
                                      &nbsp;
                                      {`${
                                        prescription.prescriber?.person
                                          ?.firstname || ""
                                      } ${
                                        prescription.prescriber?.person
                                          ?.lastname || ""
                                      }`}
                                    </Typography>
                                  </div>
                                  <div>
                                    <Typography variant="body2" component="p">
                                      <strong>
                                        <FormattedMessage
                                          id="patient.history.report.anc.dosesToDispense"
                                          defaultMessage="Doses to dispense:"
                                        />
                                      </strong>
                                      &nbsp;
                                      {dosageCount(
                                        prescription?.rate,
                                        prescription?.frequency,
                                        prescription?.dosage,
                                      )}
                                    </Typography>
                                  </div>
                                </PrescriberWrapper>
                              </Container>
                            </div>
                          ))}
                        </Section>
                      )}
                    </Section>
                  )}
                  {isImagingStudy(historicEvent) && (
                    <ImagingStudySection study={historicEvent} />
                  )}
                </div>
              );
            })}
          </div>
          {reportOption === ReportOption.WithHistory && (
            <Section>
              <h2>
                <FormattedMessage
                  id="patient.history.report.plan"
                  defaultMessage="Plan"
                />
              </h2>
              {historicEvents.map((historicEvent) =>
                isConsultation(historicEvent) && !!historicEvent.plan ? (
                  <History key={`${historicEvent.uuid}-plan`}>
                    <Typography variant="body2">
                      {historicEvent.plan}
                    </Typography>
                  </History>
                ) : null,
              )}
            </Section>
          )}
          {referrals.length > 0 ? (
            <>
              {" - "}
              <Section>
                <h2>
                  <FormattedMessage
                    id="patient.history.report.referrals.title"
                    defaultMessage="Referrals"
                  />
                </h2>
                {referrals.map(({ facilityName, reasons }) => (
                  <>
                    <h3>
                      <FormattedMessage
                        id="patient.history.report.referrals.location"
                        defaultMessage="Referral location"
                      />
                    </h3>
                    <Typography variant="body2">
                      {facilityName || "Unknown"}
                    </Typography>
                    <h3>
                      <FormattedMessage
                        id="patient.history.report.referrals.reason"
                        defaultMessage="Reason for referral"
                      />
                    </h3>
                    <Typography variant="body2">
                      {reasons.join(", ")}
                    </Typography>
                  </>
                ))}
              </Section>
            </>
          ) : null}

          <Footer>
            <FooterWrapper>
              <h3>
                <strong>{clinicianName}</strong>
              </h3>
              <FormattedMessage
                id="patient.history.report.digitallyVerified"
                defaultMessage="Digitally Verified"
              />
            </FooterWrapper>
          </Footer>
        </StyledBox>
      </PrintWrapper>
    );
  },
);

const PrescriptionDetails: FC<{
  prescription: RxPrescriptionWithDispensationsAndLegacyCreated;
}> = ({ prescription }) => {
  const { data: prescriberData } = useUserForPrescriptionStatusQuery({
    variables: { userId: prescription.prescribedByUserId },
  });
  const prescribingUser = prescriberData?.user;
  const prescribedBy = [
    prescribingUser?.person?.firstname,
    prescribingUser?.person?.lastname,
  ].join(" ");

  const { result: vmpResult } = useRxData<Vmp>(
    RXDB_VMP_COLLECTION_NAME,
    (collection) =>
      prescription.clinicalProduct?.id &&
      collection.find({ selector: { id: prescription.clinicalProduct?.id } }),
  );
  const label = vmpResult[0]?.title ?? prescription.prescribableItemFreeText;

  return (
    <Section>
      <h2>
        <FormattedMessage
          id="patient.history.report.prescription"
          defaultMessage="Prescription"
        />
      </h2>
      <div key={prescription.id}>
        <SectionTitle>
          <SubTitle>{label ?? ""}</SubTitle>
        </SectionTitle>
        <Container>
          <PrescriptionDetailsWrapper>
            <div>
              <Typography variant="body2" component="p">
                <strong>
                  <FormattedMessage
                    id="patient.history.report.datePrescribed"
                    defaultMessage="Date prescribed:"
                  />
                </strong>
                &nbsp;
                <FormattedDate value={prescription.meta.created} />
              </Typography>
            </div>
            <div>
              <Typography variant="body2" component="p">
                <strong>
                  <FormattedMessage
                    id="patient.history.report.singleDosage"
                    defaultMessage="Single dosage:"
                  />
                  &nbsp;
                </strong>
                {prescription.dosage
                  ? `${prescription.dosage.value} ${prescription.dosage.unit}`
                  : null}
              </Typography>
            </div>
          </PrescriptionDetailsWrapper>
          <PrescriberWrapper>
            <div>
              <Typography variant="body2" component="p">
                <strong>
                  <FormattedMessage
                    id="patient.history.report.prescribedBy"
                    defaultMessage="Prescribed by:"
                  />
                </strong>
                &nbsp;
                {prescribedBy}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" component="p">
                <strong>
                  <FormattedMessage
                    id="patient.history.report.dosesToDispense"
                    defaultMessage="Doses to dispense:"
                  />
                </strong>
                &nbsp;
                {dosageCount(
                  prescription?.rate,
                  prescription?.frequency,
                  prescription?.dosage,
                )}
              </Typography>
            </div>
          </PrescriberWrapper>
        </Container>
      </div>
    </Section>
  );
};

class ReactToPrintRemoteTrigger extends ReactToPrint {
  // Overrides `ReactToPrint` to remove need for triggerElement in render method
  render() {
    return <div />;
  }
}

const PrintableDoctorReport: FC<
  {
    onAfterPrint?: () => void;
  } & ComponentToPrintProps
> = ({ onAfterPrint, historicEvents, ...restProps }) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<ReactToPrintRemoteTrigger>();

  useEffect(() => {
    reportRef.current?.handleClick();
  }, []);

  return (
    <div>
      <ReactToPrintRemoteTrigger
        ref={reportRef}
        content={() => componentRef.current}
        pageStyle="@page{ size: a4; } @media print{  }"
        onAfterPrint={onAfterPrint}
      />
      <ContainerWrapper>
        <ComponentToPrint
          ref={componentRef}
          historicEvents={historicEvents}
          {...restProps}
        />
      </ContainerWrapper>
    </div>
  );
};

export default PrintableDoctorReport;
